import type { CustomTruckCapacity } from "@convex/schemas/validators/truckCapacities.lib";
import type { LoadingTruckType } from "@convex/schemas/validators/enums.lib";
import { isMemberOf, recordFromKeys } from '@shared/validation/narrow'

export const TRUCK_TYPE_SLUGS = [
  'dryVan',
  'flatbed',
  'boxTruck',
  'cargoVan',
] as const satisfies readonly LoadingTruckType[]

export type TruckTypeSlug = LoadingTruckType

export const PRIMARY_TRUCK_SLUGS = [
  'dryVan',
  'flatbed',
  'boxTruck',
] as const

export type PrimaryTruckSlug = (typeof PRIMARY_TRUCK_SLUGS)[number]

export const ADDITIONAL_TRUCK_SLUGS = [
  'cargoVan',
] as const

export type AdditionalTruckSlug = (typeof ADDITIONAL_TRUCK_SLUGS)[number]

export const FULL_TRUCKLOAD_TRUCK_SLUGS = [
  'dryVan',
  'flatbed',
] as const

export type FullTruckloadTruckSlug = (typeof FULL_TRUCKLOAD_TRUCK_SLUGS)[number]

export const TRUCK_TYPE_LABELS: Record<TruckTypeSlug, string> = {
  "dryVan": "53' Dry Van",
  "flatbed": "Flatbed",
  "boxTruck": "Box Truck (26')",
  "cargoVan": "Cargo Van"
}

export const TRUCK_TYPE_SHORT_LABELS: Record<TruckTypeSlug, string> = {
  "dryVan": "Dry Van",
  "flatbed": "Flatbed",
  "boxTruck": "Box Truck",
  "cargoVan": "Cargo Van"
}

export const TRUCK_LOADING_HINTS: Record<TruckTypeSlug, string> = {
  "dryVan": "Loads at a dock or by forklift",
  "flatbed": "Forklift loads it from the side",
  "boxTruck": "Liftgate, no dock needed",
  "cargoVan": "Loaded by hand"
}

export const TRUCK_TYPE_FULL_TRUCKLOAD_ELIGIBLE: Record<TruckTypeSlug, boolean> = {
  "dryVan": true,
  "flatbed": true,
  "boxTruck": false,
  "cargoVan": false
}

export interface TruckTypeConfig {
  slug: TruckTypeSlug
  displayName: string
  shortLabel: string
  defaultUnits: number
  category: 'primary' | 'additional'
  fullTruckloadEligible: boolean
  displayOrder: number
}

export const TRUCK_TYPES: readonly TruckTypeConfig[] = [
  {
    "slug": "dryVan",
    "displayName": "53' Dry Van",
    "shortLabel": "Dry Van",
    "defaultUnits": 680,
    "category": "primary",
    "fullTruckloadEligible": true,
    "displayOrder": 1
  },
  {
    "slug": "flatbed",
    "displayName": "Flatbed",
    "shortLabel": "Flatbed",
    "defaultUnits": 520,
    "category": "primary",
    "fullTruckloadEligible": true,
    "displayOrder": 2
  },
  {
    "slug": "boxTruck",
    "displayName": "Box Truck (26')",
    "shortLabel": "Box Truck",
    "defaultUnits": 200,
    "category": "primary",
    "fullTruckloadEligible": false,
    "displayOrder": 3
  },
  {
    "slug": "cargoVan",
    "displayName": "Cargo Van",
    "shortLabel": "Cargo Van",
    "defaultUnits": 20,
    "category": "additional",
    "fullTruckloadEligible": false,
    "displayOrder": 4
  }
]

export const PRIMARY_TRUCK_TYPES: readonly TruckTypeConfig[] = TRUCK_TYPES.filter(
  (truckType) => truckType.category === 'primary'
)

export const ADDITIONAL_TRUCK_TYPES: readonly TruckTypeConfig[] = TRUCK_TYPES.filter(
  (truckType) => truckType.category === 'additional'
)

export function isTruckTypeSlug(slug: string): slug is TruckTypeSlug {
  return isMemberOf(TRUCK_TYPE_SLUGS, slug)
}

export function isPrimaryTruckSlug(slug: string): slug is PrimaryTruckSlug {
  return isMemberOf(PRIMARY_TRUCK_SLUGS, slug)
}

export function isAdditionalTruckSlug(slug: string): slug is AdditionalTruckSlug {
  return isMemberOf(ADDITIONAL_TRUCK_SLUGS, slug)
}

export function getTruckTypeConfig(slug: TruckTypeSlug): TruckTypeConfig | undefined {
  return TRUCK_TYPES.find((truckType) => truckType.slug === slug)
}

export function getTruckTypeLabel(slug: TruckTypeSlug): string {
  return TRUCK_TYPE_LABELS[slug]
}

export function getTruckTypeShortLabel(slug: TruckTypeSlug): string {
  return TRUCK_TYPE_SHORT_LABELS[slug]
}

export interface TruckCapacity {
  enabled: boolean
  units?: number
  stacks?: number
  unitsPerStack?: number
}

export type TruckCapacities = Record<TruckTypeSlug, TruckCapacity> & { custom?: CustomTruckCapacity[] }

export function createDefaultTruckCapacities(): TruckCapacities {
  return recordFromKeys(TRUCK_TYPE_SLUGS, () => ({
    enabled: false,
  }))
}

export interface TruckTypeEntry {
  key: TruckTypeSlug
  enabled: boolean
  unitsPerLoad?: number
}

export function capacitiesToEntries(capacities: TruckCapacities): TruckTypeEntry[] {
  return TRUCK_TYPE_SLUGS.map((slug) => ({
    key: slug,
    enabled: capacities[slug]?.enabled ?? false,
    unitsPerLoad: capacities[slug]?.units,
  }))
}

export function entriesToCapacities(
  entries: TruckTypeEntry[],
  defaults: TruckCapacities
): TruckCapacities {
  const result = { ...defaults }
  for (const slug of TRUCK_TYPE_SLUGS) {
    const entry = entries.find((e) => e.key === slug)
    result[slug] = {
      enabled: entry?.enabled ?? false,
      units: entry?.unitsPerLoad ?? defaults[slug]?.units,
    }
  }
  return result
}
